<template>
  <div data-cy="deviation-survey-table">
    <div class="flex ml-4">
      <div class="flex">
        <p>Alert</p>
        <div class="striped-box red"></div>
        <p class="ml-5 mr-1">Caution</p>
        <div class="striped-box yellow"></div>
      </div>
    </div>
    <DataTable
      :value="isPreview ? displayData.slice(0, 3) : displayData"
      :class="tableClass"
      :rowClass="rowClassFn"
      :virtualScrollerOptions="{
        items: displayData,
        itemSize: rowHeight,
        showLoader: true,
        loading: false
      }"
      scrollable
      scrollHeight="400px"
      data-cy="main-datatable"
    >
      <Column
        :header="`Measured Depth (${depthUnit.label})`"
        header-style="font-weight:bold"
        bodyClass="numeral-4 md"
        field="0"
      >
        <template #body="{ data }">
          <span class="relative">
            <span
              v-if="data[5].rowIdx === equipmentStartIdx - 1"
              class="equipment-border-text start"
              >Equipment Start</span
            >
            <span v-tooltip.top="data[5].errorMsg">{{ data[0] }}</span>
            <span
              v-if="data[5].rowIdx === equipmentEndIdx"
              class="equipment-border-text end"
              >Equipment End</span
            >
          </span>
        </template>
      </Column>

      <Column
        :header="`Inclination (${angleUnit.label})`"
        header-style="font-weight:bold"
        bodyClass="numeral-4"
        field="1"
      >
        <template #body="{ data }">
          <span v-tooltip.top="data[5].errorMsg">{{ data[1] }}</span>
        </template>
      </Column>

      <Column
        v-if="is3dMdInclAz"
        :header="`Azimuth (${angleUnit.label})`"
        header-style="font-weight:bold"
        bodyClass="numeral-4"
        field="2"
      >
        <template #body="{ data }">
          <span v-tooltip.top="data[5].errorMsg">{{ data[2] }}</span>
        </template>
      </Column>

      <Column
        :header="`True Vertical Depth (${depthUnit.label})`"
        header-style="font-weight:bold"
        :bodyClass="tvdBodyClass"
        field="3"
      >
        <template #body="{ data }">
          <span v-tooltip.top="data[5].errorMsg">{{ data[3] }}</span>
        </template>
      </Column>

      <Column
        v-if="is3dMdInclAz"
        :header="`Dogleg Severity (degrees/${
          depthUnit.label === `Feet` ? `100ft` : `30.48m`
        })`"
        header-style="font-weight:bold"
        bodyClass="numeral-4"
        field="4"
      >
        <template #body="{ data }">
          <span v-tooltip="data[5].errorMsg">{{ data[4] }}</span>
        </template>
      </Column>
    </DataTable>

    <p
      v-if="isPreview && displayData.length > 3 && displayData.length < 6"
      class="my-3"
      data-cy="more-rows-preview-message"
    >
      ...{{ displayData.length - 3 }} more rows
    </p>
    <p
      v-if="isPreview && displayData.length > 6"
      class="my-3"
      data-cy="more-rows-message"
    >
      ...{{ displayData.length - 6 }} more rows
    </p>

    <DataTable
      v-if="isPreview && displayData.length > 6"
      :value="displayData.slice(-3)"
      class="hide-thead"
      data-cy="preview-datatable"
    >
      <Column
        :header="`Measured Depth (${depthUnit.label})`"
        header-style="font-weight:bold"
        bodyClass="numeral-4 surface-200"
        field="0"
      >
      </Column>
      <Column
        :header="`Inclination (${angleUnit.label})`"
        header-style="font-weight:bold"
        :bodyClass="inclinationBodyClass"
        field="1"
      >
      </Column>
      <Column
        v-if="is3dMdInclAz"
        :header="`Azimuth (${angleUnit.label})`"
        header-style="font-weight:bold"
        :bodyClass="azimuthBodyClass"
        field="2"
      >
      </Column>
      <Column
        :header="`True Vertical Depth (${depthUnit.label})`"
        header-style="font-weight:bold"
        :bodyClass="tvdBodyClass"
        field="3"
      >
      </Column>
      <Column
        v-if="is3dMdInclAz"
        :header="`Dogleg Severity (degrees/${
          depthUnit.label === `Feet` ? `100ft` : `30.48m`
        })`"
        header-style="font-weight:bold"
        bodyClass="numeral-4"
        field="4"
      >
      </Column>
    </DataTable>
  </div>
</template>
<script setup lang="ts">
import type { Deviation_Survey } from "gql/api";
import { Deviation_Survey_Directional_Dataset_Enum } from "gql/api";
import type { PropType } from "vue";
import { computed, ref } from "vue";
import { LengthUnits } from "@/libs/units/length";
import { AngleUnits } from "@/libs/units/angle";
import type { ConvertableUnit } from "@/libs/units";
import { decimalFormatter, injectStrict } from "@/utils/utils";
import { useSettingsStore } from "@/store";
import {
  scenarioCalculationsKey,
  scenarioValidatorKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import {
  DLS_INSTALL_LIMIT,
  DLS_LANDING_LIMIT,
  INCL_LANDING_LIMIT,
  SEAL_INCLINATION_LIMIT
} from "@/composables/validation/validatorConstants";
import DataTable from "primevue/datatable";

const props = defineProps({
  // needs to be statically passed in to match the style of each row height
  // so the virtual scroller can calculate the height correctly
  rowHeight: {
    type: Number,
    required: true
  },
  surveyData: {
    type: Array as PropType<Deviation_Survey["entry_rows"]>,
    default: () => []
  },
  surveyDirectionType: {
    type: String as PropType<Deviation_Survey_Directional_Dataset_Enum>,
    default: () =>
      Deviation_Survey_Directional_Dataset_Enum.ThreeDMdInclinationAzimuth
  },
  isPreview: {
    type: Boolean,
    default: () => false
  },
  depthUnit: {
    type: Object as PropType<ConvertableUnit>,
    default: () => LengthUnits.Feet
  },
  angleUnit: {
    type: Object as PropType<ConvertableUnit>,
    default: () => AngleUnits.Degrees
  }
});

const { filterByXrefIdAndRuleNames } = injectStrict(scenarioValidatorKey);
const calcs = injectStrict(scenarioCalculationsKey);

const installDlsError = filterByXrefIdAndRuleNames(["install_dls"]).value[0];
const landingDlsError = filterByXrefIdAndRuleNames(["landing_dls"]).value[0];
const landingInclError = filterByXrefIdAndRuleNames(["landing_inclination"])
  .value[0];
const landingDlsCaution = filterByXrefIdAndRuleNames(["landing_dls_caution"])
  .value[0];
const sealIncWarning = filterByXrefIdAndRuleNames(["seal_inclination_warning"])
  .value[0];
const sealIncError = filterByXrefIdAndRuleNames(["seal_inclination_error"])
  .value[0];

const equipmentStartIdx = ref(-1);
const equipmentEndIdx = ref(-1);

const rowMeta = (row: number[], rowIdx: number) => {
  const md = row[0];
  const inc = row[1];
  const dls = row[4];

  const endOfTubing = calcs.equipmentLengths.tubingLengthFt.value;
  const endOfEquipment = calcs.equipmentLengths.bottomOfEquipmentDepthFt.value;
  const endOfIntakes = calcs.equipmentLengths.bottomOfIntakesDepthFt.value;
  const endOfSeals = calcs.equipmentLengths.bottomOfSealsDepthFt.value;
  // Same as deviationSurveyRowsBotToBoe in survey checks
  const isLandingRow = md >= endOfTubing && md <= endOfEquipment;

  const isSealRow = md >= endOfIntakes && md <= endOfSeals;
  if (
    md >= calcs.equipmentLengths.tubingLengthFt.value &&
    equipmentStartIdx.value < 0
  )
    equipmentStartIdx.value = rowIdx;

  if (
    md >= calcs.equipmentLengths.bottomOfEquipmentDepthFt.value &&
    equipmentEndIdx.value < 0
  )
    equipmentEndIdx.value = rowIdx;

  let errorMsg = "";
  let severity: "none" | "warn" | "error" = "none";

  if (dls > DLS_INSTALL_LIMIT && installDlsError) {
    errorMsg += installDlsError.message + "\n";
    severity = "error";
  }

  if (isSealRow) {
    if (inc > SEAL_INCLINATION_LIMIT && sealIncError) {
      errorMsg += sealIncError.message + "\n";
      severity = "error";
    }
  }

  if (isLandingRow) {
    if (inc > INCL_LANDING_LIMIT && landingInclError) {
      errorMsg += landingInclError.message + "\n";
      severity = "error";
    }

    if (dls > DLS_LANDING_LIMIT && landingDlsError) {
      errorMsg += landingDlsError.message + "\n";
      severity = "error";
    } else if (
      dls >= 1 &&
      dls < DLS_LANDING_LIMIT &&
      landingDlsCaution &&
      severity === "none"
    ) {
      errorMsg += landingDlsCaution.message + "\n";
      severity = "warn";
    }
    if (inc > SEAL_INCLINATION_LIMIT && sealIncWarning && !sealIncError) {
      if (severity === "none") {
        severity = "warn";
      }
      errorMsg += sealIncWarning.message + "\n";
    }
  }

  return { errorMsg: errorMsg.trim(), rowIdx, severity };
};

const is2dMdTvd = computed(
  () =>
    props.surveyDirectionType ===
    Deviation_Survey_Directional_Dataset_Enum.TwoDMdTvd
);

const is3dMdInclAz = computed(
  () =>
    props.surveyDirectionType ===
    Deviation_Survey_Directional_Dataset_Enum.ThreeDMdInclinationAzimuth
);

const inclinationBodyClass = computed(
  () => `numeral-4 ${!is2dMdTvd.value ? "surface-200" : ""}`
);

const azimuthBodyClass = computed(
  () => `numeral-4 ${is3dMdInclAz.value ? "surface-200" : ""}`
);

const tvdBodyClass = computed(
  () => `numeral-4 ${is2dMdTvd.value ? "surface-200" : ""}`
);

const tableClass = computed(() => `${props.isPreview ? "" : "m-3 shadow-2"}`);

const { userSettingsNumberFormat, isDarkMode } = useSettingsStore();

// Since we're not using converterInputs, this is how we're switching units
// for the survey data
const displayData = computed(() => {
  // DLS doesn't get converted since it's a ratio and we already
  // have it calculated
  return (props.surveyData ?? []).map((row: number[], rowIdx) => {
    return [
      decimalFormatter(
        props.depthUnit.toThisUnit(row[0]),
        true,
        2,
        0,
        userSettingsNumberFormat.value
      ),
      decimalFormatter(
        props.angleUnit.toThisUnit(row[1]),
        true,
        2,
        0,
        userSettingsNumberFormat.value
      ),
      decimalFormatter(
        props.angleUnit.toThisUnit(row[2]),
        true,
        2,
        0,
        userSettingsNumberFormat.value
      ),
      decimalFormatter(
        props.depthUnit.toThisUnit(row[3]),
        true,
        2,
        0,
        userSettingsNumberFormat.value
      ),
      decimalFormatter(row[4], true, 2, 0, userSettingsNumberFormat.value),
      rowMeta(row, rowIdx)
    ];
  });
});

const stripeColor = computed(() =>
  isDarkMode.value ? "var(--danger-700)" : "var(--danger-200)"
);
const warningStripeColor = computed(() =>
  isDarkMode.value ? "var(--warning-700)" : "var(--warning-200)"
);

function rowClassFn(
  data: [
    string,
    string,
    string,
    string,
    string,
    { severity: "none" | "warn" | "error"; rowIdx: number }
  ]
) {
  return {
    "red-grid": data[5].severity === "error",
    "yellow-grid": data[5].severity === "warn",
    "equipment-border":
      data[5].rowIdx === equipmentStartIdx.value ||
      data[5].rowIdx === equipmentEndIdx.value
  };
}
</script>

<style lang="scss" scoped>
:deep(.hide-thead) {
  .p-datatable-thead {
    display: none;
  }
}

:deep(.md) {
  background-color: var(--surface-200);
}

.striped-box {
  width: 40px;
  height: 40px;
  border-radius: 5px;

  &.red {
    background-image: repeating-linear-gradient(
      -45deg,
      v-bind(stripeColor),
      v-bind(stripeColor) 2px,
      transparent 2px,
      transparent 6px
    );
  }

  &.yellow {
    background-image: repeating-linear-gradient(
      -45deg,
      v-bind(warningStripeColor),
      v-bind(warningStripeColor) 2px,
      transparent 2px,
      transparent 6px
    );
  }
}

/* highlight whole row */
:deep(.yellow-grid) {
  background-image: repeating-linear-gradient(
    -51deg,
    v-bind(warningStripeColor),
    v-bind(warningStripeColor) 1.5px,
    transparent 1.5px,
    transparent 16px
  );

  & > td.md {
    background-color: transparent;
  }
}

:deep(.equipment-border > td) {
  border-top: 2px var(--info-600) solid;
}
.equipment-border-text {
  position: absolute;
  left: 0;
  width: 200px;
  color: var(--info-600);
  &.start {
    top: 17px;
  }
  &.end {
    top: -17px;
  }
}
:deep(.red-grid) {
  background-image: repeating-linear-gradient(
    -51deg,
    v-bind(stripeColor),
    v-bind(stripeColor) 1.5px,
    transparent 1.5px,
    transparent 16px
  );
  & > td.md {
    background-color: transparent;
  }
}
</style>
